import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Star } from "lucide-react";

const GRANT_OPTIONS = [
  { v: "workshop", l: "Workshop access" },
  { v: "course", l: "Course access" },
  { v: "membership", l: "Membership access" },
  { v: "cohort", l: "Cohort access" },
];

export default function TiersAdminTab({ onChange }) {
  const [tiers, setTiers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [form, setForm] = useState(blank());

  const load = async () => {
    const { data } = await api.get("/admin/tiers");
    setTiers(data);
  };
  useEffect(() => { load(); }, []);

  const open = (t) => {
    setEditingKey(t ? t.tier_key : null);
    setForm(t ? {
      tier_key: t.tier_key, name: t.name, price: t.price,
      price_cents: t.price_cents || 0, cadence: t.cadence || "one-time",
      sub: t.sub || "", features: (t.features || []).join("\n"),
      popular: !!t.popular, sort_order: t.sort_order || 100,
      active: t.active !== false, grants_tier: t.grants_tier || "workshop",
    } : blank());
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        tier_key: form.tier_key.trim(),
        name: form.name.trim(),
        price: form.price.trim(),
        price_cents: Number(form.price_cents) || 0,
        cadence: form.cadence || "one-time",
        sub: form.sub,
        features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
        popular: !!form.popular,
        sort_order: Number(form.sort_order) || 100,
        active: !!form.active,
        grants_tier: form.grants_tier,
      };
      if (editingKey) {
        await api.patch(`/admin/tiers/${editingKey}`, payload);
        toast.success("Tier updated");
      } else {
        await api.post("/admin/tiers", payload);
        toast.success("Tier created");
      }
      setShowForm(false);
      setEditingKey(null);
      onChange?.();
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"?`)) return;
    try {
      await api.delete(`/admin/tiers/${t.tier_key}`);
      toast.success("Deleted");
      onChange?.();
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl">Tiers & Add-Ons ({tiers.length})</h2>
          <p className="text-sm text-white/55 mt-1 max-w-2xl">
            Edit pricing, features, and display for your 4 core tiers — or create new add-on offers (e.g. Peptide-specific packs). Every tier grants access to one of the 5 content levels.
          </p>
        </div>
        <Button onClick={() => open(null)} data-testid="new-tier-btn"
          className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
          <Plus size={14} className="mr-2" /> New tier
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiers.map((t) => (
          <div key={t.tier_key} data-testid={`tier-card-${t.tier_key}`}
            className={`rounded-2xl p-5 border ${t.popular ? "border-[#FF8A00]/40 bg-gradient-to-b from-[#FF8A00]/8 to-transparent" : "border-white/5 bg-[#0A0A0A]"}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-display text-xl">{t.name}</div>
                  {t.popular && <Star size={14} className="text-[#FF8A00]" />}
                  {!t.active && <span className="text-[9px] tracking-[0.2em] uppercase text-white/40">inactive</span>}
                </div>
                <div className="text-[11px] text-white/40 mt-0.5 font-mono">{t.tier_key} → grants: {t.grants_tier}</div>
                <div className="font-display text-3xl mt-2">
                  {t.price}<span className="text-xs text-white/40 ml-2">{t.cadence}</span>
                </div>
                <p className="text-sm text-white/55 mt-2 line-clamp-2">{t.sub}</p>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button data-testid={`edit-tier-${t.tier_key}`} onClick={() => open(t)}
                  className="text-white/40 hover:text-white p-2">
                  <Pencil size={14} />
                </button>
                <button data-testid={`delete-tier-${t.tier_key}`} onClick={() => remove(t)}
                  className="text-white/40 hover:text-[#E52E71] p-2">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            <ul className="mt-3 space-y-1 text-sm text-white/70">
              {(t.features || []).slice(0, 4).map((f, i) => (
                <li key={i} className="line-clamp-1">· {f}</li>
              ))}
              {(t.features || []).length > 4 && (
                <li className="text-[11px] text-white/40">+{t.features.length - 4} more</li>
              )}
            </ul>
          </div>
        ))}
      </div>

      <Dialog open={showForm} onOpenChange={(v) => { if (!v) { setShowForm(false); setEditingKey(null); } }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-xl max-h-[90vh] overflow-y-auto" data-testid="tier-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editingKey ? "Edit tier" : "New tier"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-white/60">Tier key (slug)</Label>
                <Input value={form.tier_key} onChange={(e) => setForm({...form, tier_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_")})}
                  required data-testid="tier-key"
                  placeholder="e.g. gut_reset" className="bg-[#0F0F0F] border-white/10 mt-1 font-mono"/>
              </div>
              <div>
                <Label className="text-xs text-white/60">Display name</Label>
                <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                  required data-testid="tier-name"
                  className="bg-[#0F0F0F] border-white/10 mt-1"/>
              </div>
              <div>
                <Label className="text-xs text-white/60">Price (display)</Label>
                <Input value={form.price} onChange={(e) => setForm({...form, price: e.target.value})}
                  placeholder="$147" required data-testid="tier-price"
                  className="bg-[#0F0F0F] border-white/10 mt-1"/>
              </div>
              <div>
                <Label className="text-xs text-white/60">Price (cents, for analytics)</Label>
                <Input type="number" value={form.price_cents}
                  onChange={(e) => setForm({...form, price_cents: e.target.value})}
                  className="bg-[#0F0F0F] border-white/10 mt-1"/>
              </div>
              <div>
                <Label className="text-xs text-white/60">Cadence</Label>
                <Select value={form.cadence} onValueChange={(v) => setForm({...form, cadence: v})}>
                  <SelectTrigger className="bg-[#0F0F0F] border-white/10 mt-1" data-testid="tier-cadence">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    <SelectItem value="one-time">one-time</SelectItem>
                    <SelectItem value="per month">per month</SelectItem>
                    <SelectItem value="per year">per year</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white/60">Grants access</Label>
                <Select value={form.grants_tier} onValueChange={(v) => setForm({...form, grants_tier: v})}>
                  <SelectTrigger className="bg-[#0F0F0F] border-white/10 mt-1" data-testid="tier-grants">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {GRANT_OPTIONS.map((g) => (
                      <SelectItem key={g.v} value={g.v}>{g.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-white/60">Sort order</Label>
                <Input type="number" value={form.sort_order}
                  onChange={(e) => setForm({...form, sort_order: e.target.value})}
                  className="bg-[#0F0F0F] border-white/10 mt-1"/>
              </div>
              <div className="flex items-end gap-4">
                <div>
                  <Label className="text-xs text-white/60">Popular</Label>
                  <div className="mt-2">
                    <Switch checked={form.popular} onCheckedChange={(v) => setForm({...form, popular: v})} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Active</Label>
                  <div className="mt-2">
                    <Switch checked={form.active} onCheckedChange={(v) => setForm({...form, active: v})} />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs text-white/60">Sub-headline</Label>
              <Input value={form.sub} onChange={(e) => setForm({...form, sub: e.target.value})}
                className="bg-[#0F0F0F] border-white/10 mt-1"/>
            </div>
            <div>
              <Label className="text-xs text-white/60">Features (one per line)</Label>
              <Textarea value={form.features} onChange={(e) => setForm({...form, features: e.target.value})}
                rows={5} data-testid="tier-features"
                className="bg-[#0F0F0F] border-white/10 mt-1 font-mono text-sm"/>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" onClick={() => { setShowForm(false); setEditingKey(null); }}
                variant="outline"
                className="rounded-full bg-transparent border-white/10">Cancel</Button>
              <Button type="submit" data-testid="tier-submit"
                className="rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71]">
                {editingKey ? "Save" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function blank() {
  return {
    tier_key: "", name: "", price: "", price_cents: 0,
    cadence: "one-time", sub: "", features: "",
    popular: false, sort_order: 100, active: true,
    grants_tier: "workshop",
  };
}
